import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { ClientService } from 'src/app/services/client.services';
import { DialogBackService } from 'src/app/shared/services/dialog-back.service';
import { NavigationHistoryService } from 'src/app/shared/services/navigation-history.service';
import { UserService } from 'src/app/shared/services/user.service';

interface Crumb {
  label: string;
  url?: string;
}

/**
 * Route data for pages that belong to a parent record (route `data.crumbs`): the chain above the page and the
 * query params each level needs. `{client}` is replaced by the client's name. Example, client history:
 * crumbs: [{ label: 'Clientes', path: 'clientList' }, { label: '{client}', path: 'clients/clientDetailView', keep: ['id'] }]
 */
export interface CrumbData {
  label: string;
  path: string;
  keep?: string[];
}

/** Sub-sections of a client: one URL each, shown as tabs on every client page. */
const CLIENT_TABS = [
  { label: 'Perfil', path: 'clients/clientDetailView' },
  { label: 'Editar perfil', path: 'clients/clientDetail', permission: 'add:client' },
  { label: 'Histórico', path: 'clients/clientHistory', permission: 'read:clienthistory' },
  { label: 'DNI/CUIT', path: 'clients/clientIdentificationNumber', permission: 'edit:identificationNumber' },
];

/**
 * "← Volver" + breadcrumb on every second-level page (route data.breadcrumb.level >= 2).
 * Volver goes to the PARENT of the page: a client's sub-section (Editar perfil, Histórico, DNI/CUIT, a domicilio)
 * goes back to that client, the client goes back to the list (with the search and page it had). When the parent is
 * the previous history entry it is a real Back, so the browser's Back button and Volver always agree.
 */
@Component({
  selector: 'app-back-bar',
  imports: [MatButtonModule, MatIconModule, RouterLink],
  template: `
    @if (crumbs.length > 1) {
      <nav class="tp-back-bar" aria-label="Ubicación">
        <button mat-button type="button" class="tp-back" (click)="back()" data-tp-back>
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          <span>Volver</span>
        </button>
        <ol class="tp-breadcrumb">
          @for (crumb of crumbs; track $index; let last = $last) {
            <li>
              @if (crumb.url && !last) {
                <a [routerLink]="routerPath(crumb.url)" [queryParams]="routerQuery(crumb.url)">{{ crumb.label }}</a>
              } @else {
                <span [attr.aria-current]="last ? 'page' : null">{{ crumb.label }}</span>
              }
            </li>
          }
        </ol>
      </nav>
      @if (tabs.length) {
        <nav class="tp-client-tabs" aria-label="Secciones del cliente">
          @for (tab of tabs; track tab.path) {
            <a [routerLink]="'/' + tab.path" [queryParams]="{ id: clientId }" [class.active]="tab.active"
               [attr.aria-current]="tab.active ? 'page' : null" [replaceUrl]="!tab.fromProfile">{{ tab.label }}</a>
          }
        </nav>
      }
    }
  `,
  styles: [
    `
      /* Same container as .scr-page / .tp-page content: page max width, centered, no extra gutter (the shell
         already pads the content area), so Volver, the breadcrumb and the tabs line up with the page title. */
      .tp-back-bar,
      .tp-client-tabs {
        box-sizing: border-box;
        width: 100%;
        max-width: var(--tp-page-max-width);
        margin: var(--tp-space-2) auto 0;
        padding: 0;
        text-align: start;
        font-family: var(--tp-font-family);
      }
      .tp-back-bar {
        display: flex;
        align-items: center;
        gap: var(--tp-space-3);
        flex-wrap: wrap;
      }
      /* Text-button look; the negative margin keeps the arrow on the page's left edge. */
      .tp-back.mat-mdc-button-base {
        margin-inline-start: calc(-1 * var(--tp-space-3));
        padding-inline: var(--tp-space-3);
      }
      .tp-back mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
      .tp-breadcrumb {
        display: flex;
        flex-wrap: wrap;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: var(--tp-fs-sm);
        color: var(--tp-color-text-muted);
      }
      .tp-breadcrumb li + li::before {
        content: '›';
        margin: 0 var(--tp-space-2);
      }
      .tp-breadcrumb a {
        color: var(--tp-color-primary);
        text-decoration: none;
      }
      .tp-breadcrumb a:hover {
        text-decoration: underline;
      }
      .tp-client-tabs {
        display: flex;
        gap: var(--tp-space-1);
        overflow-x: auto;
        border-bottom: 1px solid var(--tp-color-border);
      }
      .tp-client-tabs a {
        padding: var(--tp-space-2) var(--tp-space-3);
        font-size: var(--tp-fs-md);
        color: var(--tp-color-text-muted);
        text-decoration: none;
        white-space: nowrap;
        border-bottom: 2px solid transparent;
      }
      .tp-client-tabs a:first-child {
        padding-inline-start: 0;
      }
      .tp-client-tabs a.active {
        color: var(--tp-color-primary);
        border-bottom-color: var(--tp-color-primary);
        font-weight: var(--tp-fw-medium);
      }
    `,
  ],
})
export class BackBarComponent {
  private readonly router = inject(Router);
  private readonly history = inject(NavigationHistoryService);
  private readonly clientSrv = inject(ClientService);
  private readonly usrSvc = inject(UserService);

  crumbs: Crumb[] = [];
  tabs: { label: string; path: string; active: boolean; fromProfile: boolean }[] = [];
  clientId: string | null = null;
  private parentUrl?: string;
  private readonly clientNames = new Map<string, string>();

  constructor() {
    inject(DialogBackService).start();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe(() => this.update());
    // After a reload the first navigation can end before this bar exists (the header renders later).
    this.update();
  }

  back() {
    this.history.goTo(this.parentUrl ?? '/welcome');
  }

  routerPath(url: string) {
    return url.split('?')[0];
  }

  routerQuery(url: string) {
    const query: Record<string, string> = {};
    new URLSearchParams(url.split('?')[1] ?? '').forEach((value, key) => (query[key] = value));
    return query;
  }

  private update() {
    let leaf: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    while (leaf.firstChild) leaf = leaf.firstChild;

    const data = leaf.data ?? {};
    const level = data['breadcrumb']?.level ?? 0;
    this.tabs = [];
    this.clientId = null;
    if (level < 2) {
      this.crumbs = [];
      this.parentUrl = undefined;
      return;
    }

    const chain = data['crumbs'] as CrumbData[] | undefined;
    if (chain) {
      this.updateFromChain(leaf, chain, data['section']);
      return;
    }

    const parentPath = data['menu']?.pathParent;
    const parent = this.router.config.find((r) => r.path === parentPath);
    const parentLabel = parent?.data?.['menu']?.description ?? parent?.data?.['breadcrumb']?.description;
    const label = data['breadcrumb']?.description || (typeof leaf.title === 'string' ? leaf.title : '');
    this.parentUrl = parentPath ? this.history.lastUrlOf(parentPath) ?? '/' + parentPath : undefined;
    this.crumbs = [
      ...(parentLabel ? [{ label: parentLabel, url: this.parentUrl }] : []),
      ...(label ? [{ label }] : []),
    ];
  }

  private updateFromChain(leaf: ActivatedRouteSnapshot, chain: CrumbData[], section: string | undefined) {
    const params = leaf.queryParamMap;
    const id = params.get('id');
    const crumbs: Crumb[] = [];
    for (const level of chain) {
      const keep = level.keep ?? [];
      if (keep.some((k) => !params.get(k))) continue; // e.g. "new client": no client level above
      const query = new URLSearchParams(keep.map((k) => [k, params.get(k)!])).toString();
      const url = keep.length ? `/${level.path}?${query}` : this.history.lastUrlOf(level.path) ?? '/' + level.path;
      crumbs.push({ label: this.label(level.label, id), url });
    }
    crumbs.push({ label: this.label(section ?? (id ? 'Detalle' : 'Nuevo cliente'), id) });
    this.crumbs = crumbs;
    this.parentUrl = crumbs[crumbs.length - 2]?.url;

    if (id && leaf.data['clientTabs']) {
      this.clientId = id;
      const current = NavigationHistoryService.path(this.router.url);
      this.tabs = CLIENT_TABS.filter((t) => !t.permission || this.usrSvc.hasPermission(t.permission)).map((t) => ({
        label: t.label,
        path: t.path,
        active: t.path === current,
        // From the profile a sub-section is a new step (Back returns to the profile); between sub-sections it replaces.
        fromProfile: current === 'clients/clientDetailView',
      }));
    }
  }

  private label(text: string, id: string | null) {
    if (!text.includes('{client}')) return text;
    const name = id ? this.clientNames.get(id) : undefined;
    if (!name && id) {
      this.clientNames.set(id, 'Cliente');
      this.clientSrv.getClientProfile(id).subscribe({
        next: (client) => {
          this.clientNames.set(id, client?.name || 'Cliente');
          this.update();
        },
      });
    }
    return text.replace('{client}', name ?? 'Cliente');
  }
}
